import { useQuery } from '@tanstack/react-query';
import { getNavBadges, type NavBadgeCounts } from '@/features/coportal/coportal.api';

const EMPTY: NavBadgeCounts = { modifications: 0, noShow: 0, vehicles: 0 };

/**
 * Poll interval for the rail's attention pills. Matches the PHP header's
 * `_cachedQuery(..., 120, ...)` on the welcome-meetings SLA badge — the badge
 * these two are modelled on. Two minutes is slow enough that the rail costs
 * nothing and fresh enough that a queue does not look stale across a shift.
 */
const REFRESH_MS = 120_000;

/**
 * How long a count is trusted without re-asking. Navigation inside the module
 * re-runs this hook constantly, so without a staleTime every route change
 * fired a fresh request (see EGRESS note below).
 */
const STALE_MS = 60_000;

export const NAV_BADGES_QUERY_KEY = ['coportal', 'nav-badges'] as const;

/**
 * Counts for the CS/OPS rail pills (Modifications, No-show, In-house Vehicles).
 *
 * EGRESS (2026-09-22): this was a raw `useEffect` + `setInterval` keyed on
 * `pathname`, so every navigation inside CS/OPS tore the timer down and fired
 * an immediate refetch, and every mounted copy kept its own timer with no
 * shared cache. Measured on PROD it ran at ~5 calls/min against a documented
 * 120s interval, and each call costs the API three PostgREST round-trips
 * (token check + the two counts) — roughly 9,000 requests across a workday
 * for two decorative numbers.
 *
 * React Query fixes both halves: one shared cache entry deduplicates every
 * mount, and `staleTime` means a navigation re-reads the cache instead of the
 * network. The freshness the `pathname` dependency was reaching for is kept by
 * `refetchOnMount` + `refetchOnWindowFocus` — acting on a queue navigates or
 * re-focuses, so the pill still updates, just not five times a minute.
 * Invalidate `NAV_BADGES_QUERY_KEY` to force an immediate update after a write.
 */
export function useNavBadges(enabled: boolean): NavBadgeCounts {
  const { data } = useQuery({
    queryKey: NAV_BADGES_QUERY_KEY,
    queryFn: ({ signal }) => getNavBadges(signal),
    enabled,
    staleTime: STALE_MS,
    refetchInterval: REFRESH_MS,
    // A tab nobody is looking at does not need current pills.
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    // A badge is decoration: a failed count leaves the last good numbers on
    // screen rather than flashing the rail back to zero, which would read as
    // "queue cleared".
    retry: false,
    placeholderData: (prev) => prev,
  });

  return data ?? EMPTY;
}
